/**
 * ScheduledOperationsCenterPage — the operator's read-only Scheduled Operations Center
 * (DIGEMP-SCHEDULED-OPS-V2-FRONTEND-088; canonical V2 over the unmerged FRONTEND-061).
 *
 * Answers one workflow end to end: what is scheduled, when does each job run next, which Digital
 * Employee owns it, what happened on its last run, what failed, and where to go next. The page
 * performs no schedule action (create/edit/toggle/delete/run-now stay on the admin management
 * page at /scheduled-checks); every drill-down link lands on an existing screen where the current
 * permission gates (agents_view for runs, admin for schedule management) already apply.
 *
 * Data comes from two existing endpoints only:
 *   - GET /api/scheduled-checks/ (admin-only) — authoritative per-check state incl. latest run.
 *   - GET /api/agent-runs/?initiated_by_type=auto:scheduled_check&limit=200 (agents_view) —
 *     run evidence, the failure list and the unmatched system-run bucket (e.g. Guardian posture
 *     runs carry context.system_check=true but no scheduled_check_id).
 * The page adds no api.js entry and no backend contract.
 *
 * Truthfulness rules:
 *   - A failed/forbidden checks fetch never renders as "no scheduled checks".
 *   - A failed/forbidden runs fetch says the history is unavailable and cannot claim "no failures".
 *   - A check whose latest run falls outside the loaded 200-run window says so instead of
 *     attributing the newest loaded run to it.
 *   - Raw run context is never rendered; only result_summary, bounded action lines, scalar times
 *     and a link built from context.conversation_id are surfaced, all redacted via safeText.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentRunsApi, agentsApi, scheduledChecksApi } from "../services/api";
import { scheduleHealth, INTERVAL_OPTIONS, RUN_STATUS, HEALTH_DOT, fetchFailureKind, FETCH_FAILURE_TEXT, runStatusTone } from "./ScheduledChecksPage";
import EmployeeAvatar from "../components/EmployeeAvatar";

// One bounded runs fetch backs every evidence view; when it is the limiting factor the page says so.
const RUNS_LIMIT = 200;
const UPCOMING_SHOWN = 5;
const RECENT_RUNS_PER_CHECK = 5;
const FAILURES_SHOWN = 5;
const UNMATCHED_SHOWN = 5;
const SUMMARY_MAX = 300;
const ACTION_MAX = 140;

const FAILED_STATUSES = new Set(["failed", "rejected"]);

const RISK_HE = { green: "נמוך", amber: "בינוני", red: "גבוה" };
const OUTCOME_HE = { proposed: "מוצע", approved: "אושר", rejected: "נדחה", succeeded: "הצליח", failed: "נכשל" };

// Attention-first, mirroring the workspace section's ranking: a check whose next run is already
// late or whose last run failed outranks a healthy one; an intentionally paused check ranks last.
const HEALTH_RANK = { overdue: 0, failed: 1, pending: 2, never: 3, running: 4, ok: 5, paused: 6 };

const TILES = [
  { key: "overdue", label: "מאחרות", cls: "bg-orange-50 border-orange-200 text-orange-700" },
  { key: "failed", label: "נכשלו", cls: "bg-red-50 border-red-200 text-red-700" },
  { key: "pending", label: "ממתינות לפעולה", cls: "bg-amber-50 border-amber-200 text-amber-700" },
  { key: "running", label: "בהרצה כעת", cls: "bg-blue-50 border-blue-200 text-blue-700" },
  { key: "paused", label: "מושהות", cls: "bg-gray-100 border-gray-200 text-gray-600" },
];

const RUNS_UNAVAILABLE_TEXT = "היסטוריית ההרצות אינה זמינה כרגע — לא ניתן לקבוע מה הייתה התוצאה או אם היו כשלים.";
const RUNS_FORBIDDEN_TEXT = "אין הרשאה לצפות בהיסטוריית ההרצות (נדרשת הרשאת agents_view) — התוצאות והכשלים אינם מוצגים.";
// An unreachable server reads differently from a failed load — same vocabulary as the checks.
const RUNS_UNAVAILABLE_BY_KIND = {
  offline: "היסטוריית ההרצות אינה זמינה כרגע — לא התקבלה תשובה מהשרת, ולכן לא ניתן לקבוע מה הייתה התוצאה או אם היו כשלים.",
  failed: RUNS_UNAVAILABLE_TEXT,
};
const runsUnavailableText = (kind) => RUNS_UNAVAILABLE_BY_KIND[kind] || RUNS_UNAVAILABLE_TEXT;
const NO_FAILURES_TEXT = `לא תועדו כשלים ב-${RUNS_LIMIT} ההרצות המתוזמנות האחרונות שנטענו.`;

// ─── Pure derivations (exported for focused tests) ───────────────────────────

// Secrets must never reach the DOM: a line matching a credential-ish shape is replaced wholesale
// rather than partially masked, so no fragment of it can leak (same rule as the workspace section).
export function safeText(raw, max) {
  if (typeof raw !== "string" || raw.trim() === "") return null;
  if (/secret|token|password|api[_-]?key|private[_-]?key|bearer|authorization|credential/i.test(raw)) {
    return "התוכן הוסתר — זוהה מידע רגיש";
  }
  const trimmed = raw.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
}

export function runsForCheck(runs, checkId) {
  if (!Array.isArray(runs) || checkId == null) return [];
  const id = String(checkId);
  return runs
    .filter((run) => String(run?.context?.scheduled_check_id ?? "") === id)
    .sort((a, b) => (Date.parse(b?.created_at) || 0) - (Date.parse(a?.created_at) || 0));
}

export function unmatchedSystemRuns(runs) {
  if (!Array.isArray(runs)) return [];
  return runs.filter((run) => run?.context?.scheduled_check_id == null);
}

export function failedRuns(runs) {
  if (!Array.isArray(runs)) return [];
  return runs
    .filter((run) => FAILED_STATUSES.has(run?.status))
    .sort((a, b) => (Date.parse(b?.created_at) || 0) - (Date.parse(a?.created_at) || 0));
}

export function sortChecksForOps(checks) {
  return [...checks].sort((a, b) => {
    const ra = HEALTH_RANK[scheduleHealth(a)] ?? 9;
    const rb = HEALTH_RANK[scheduleHealth(b)] ?? 9;
    if (ra !== rb) return ra - rb;
    const na = Date.parse(a?.next_run_at || "");
    const nb = Date.parse(b?.next_run_at || "");
    if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
    if (Number.isNaN(na)) return 1;
    if (Number.isNaN(nb)) return -1;
    return na - nb;
  });
}

export function upcomingChecks(checks, limit = UPCOMING_SHOWN) {
  return checks
    .filter((c) => c.is_enabled && c.next_run_at)
    .sort((a, b) => Date.parse(a.next_run_at) - Date.parse(b.next_run_at))
    .slice(0, limit);
}

export function formatRelativeHe(targetIso, now) {
  const target = Date.parse(targetIso || "");
  if (Number.isNaN(target)) return null;
  const diff = target - now;
  const abs = Math.abs(diff);
  const minutes = Math.floor(abs / 60_000);
  let label;
  if (minutes < 1) label = "פחות מדקה";
  else if (minutes < 60) label = minutes === 1 ? "דקה" : `${minutes} דקות`;
  else {
    const hours = Math.floor(minutes / 60);
    if (hours < 24) label = hours === 1 ? "שעה" : `${hours} שעות`;
    else {
      const days = Math.floor(hours / 24);
      label = days === 1 ? "יום" : `${days} ימים`;
    }
  }
  return diff >= 0 ? `בעוד ${label}` : `באיחור של ${label}`;
}

export function conversationIdOf(run) {
  const id = run?.context?.conversation_id;
  return typeof id === "string" && id.length > 0 && id.length <= 64 ? id : null;
}

export function fmtAbs(dt) {
  if (!dt) return null;
  const parsed = Date.parse(dt);
  if (Number.isNaN(parsed)) return null;
  return new Date(parsed).toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" });
}

export function intervalLabel(hours) {
  return INTERVAL_OPTIONS.find((o) => o.value === hours)?.label || (hours != null ? `${hours} שעות` : null);
}

function statusLabel(status) {
  if (!status) return null;
  return RUN_STATUS[status] || status;
}

function fmtDuration(ms) {
  if (!Number.isFinite(ms)) return null;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} שנ׳`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} דק׳`;
  return `${Math.floor(minutes / 60)} שע׳ ${minutes % 60} דק׳`;
}

// ─── Building blocks ─────────────────────────────────────────────────────────

function HealthDot({ check }) {
  const meta = HEALTH_DOT[scheduleHealth(check)] || HEALTH_DOT.never;
  return <span className={`w-2 h-2 rounded-full flex-shrink-0 inline-block ${meta.cls}`} title={meta.title} aria-label={meta.title} />;
}

function NextRunCell({ check, now }) {
  if (!check.is_enabled) return <span className="text-gray-400">מושהה</span>;
  const abs = fmtAbs(check.next_run_at);
  if (!abs) return <span className="text-gray-400">לא נקבע מועד</span>;
  const rel = formatRelativeHe(check.next_run_at, now);
  const late = rel != null && rel.startsWith("באיחור");
  return (
    <span className={late ? "text-orange-600" : "text-gray-600"}>
      {abs}
      {rel && <span className="text-xs text-gray-400"> · {rel}</span>}
    </span>
  );
}

function ActionLine({ action }) {
  const desc = safeText(action?.description, ACTION_MAX) || "פעולה ללא תיאור";
  const risk = RISK_HE[action?.risk_level] || action?.risk_level;
  const outcome = OUTCOME_HE[action?.outcome] || action?.outcome;
  const meta = [risk ? `סיכון: ${risk}` : null, outcome ? `תוצאה: ${outcome}` : null].filter(Boolean).join(" · ");
  return (
    <li className="text-xs text-gray-600">
      {desc}
      {meta && <span className="text-gray-400"> — {meta}</span>}
    </li>
  );
}

function RunEvidence({ run }) {
  const summary = safeText(run?.result_summary, SUMMARY_MAX);
  const actions = Array.isArray(run?.actions) ? run.actions.slice(0, 5) : [];
  const conversationId = conversationIdOf(run);
  const started = fmtAbs(run?.started_at);
  const finished = fmtAbs(run?.finished_at);
  const duration = fmtDuration(run?.duration_ms);
  return (
    <div className="space-y-2 text-sm">
      <div>
        <span className="font-medium text-gray-700">הרצה אחרונה: </span>
        <Link to={`/agent-runs/${run.id}`} className={`hover:underline ${runStatusTone(run.status)}`}>
          {statusLabel(run.status) || "ריצה"}
        </Link>
        <span className="text-gray-500"> · {fmtAbs(run.created_at) || "זמן לא תועד"}</span>
      </div>
      <div>
        <span className="font-medium text-gray-700">סיכום תוצאה: </span>
        {summary ? (
          <span className="text-gray-600">{summary}</span>
        ) : (
          <span className="text-gray-400 italic">לא סופק סיכום תוצאה</span>
        )}
      </div>
      {(started || finished || duration) && (
        <div className="text-xs text-gray-500">
          {started && <span>התחלה: {started}</span>}
          {finished && <span>{started ? " · " : ""}סיום: {finished}</span>}
          {duration && <span>{(started || finished) ? " · " : ""}משך: {duration}</span>}
        </div>
      )}
      <div>
        <div className="font-medium text-gray-700">פעולות שבוצעו ({actions.length})</div>
        {actions.length === 0 ? (
          <p className="text-xs text-gray-400">לא תועדו פעולות בהרצה זו.</p>
        ) : (
          <ul className="list-disc pr-5 space-y-0.5">
            {actions.map((action) => (
              <ActionLine key={action.id ?? action.sequence_no} action={action} />
            ))}
          </ul>
        )}
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        <Link to={`/agent-runs/${run.id}`} className="text-brand-600 hover:underline">
          לריצה המלאה
        </Link>
        {run.ticket_id && (
          <Link to={`/tickets/${run.ticket_id}`} className="text-brand-600 hover:underline">
            {run.ticket_number != null ? `לקריאה #${run.ticket_number}` : "לקריאה שיצרה הריצה"}
          </Link>
        )}
        {conversationId && (
          <Link to={`/conversations/${conversationId}`} className="text-brand-600 hover:underline">
            לשיחה
          </Link>
        )}
      </div>
    </div>
  );
}

function CheckEvidenceRow({ check, runsState, runs, colSpan }) {
  let body;
  if (runsState.loading) {
    body = <p className="text-sm text-gray-400">טוען עדויות הרצה…</p>;
  } else if (runsState.forbidden) {
    body = <p className="text-sm text-amber-700">{RUNS_FORBIDDEN_TEXT}</p>;
  } else if (runsState.error) {
    body = <p className="text-sm text-amber-700">{runsUnavailableText(runsState.kind)}</p>;
  } else {
    const list = runsForCheck(runs, check.id);
    const last = list[0] ?? null;
    if (!last) {
      body = (
        <p className="text-sm text-gray-400">
          {check.latest_run_id
            ? `ההרצה האחרונה אינה נכללת ב-${RUNS_LIMIT} ההרצות האחרונות שנטענו — ייתכן שהיא ותיקה מהחלון.`
            : "לא נמצאו הרצות המשויכות לבדיקה זו."}
        </p>
      );
    } else {
      body = (
        <div className="space-y-3">
          <RunEvidence run={last} />
          <div>
            <div className="font-medium text-gray-700 text-sm">הרצות אחרונות בבדיקה ({list.length})</div>
            <ul className="mt-1 space-y-0.5">
              {list.slice(0, RECENT_RUNS_PER_CHECK).map((run) => (
                <li key={run.id} className="text-xs">
                  <Link to={`/agent-runs/${run.id}`} className={`hover:underline ${runStatusTone(run.status)}`}>
                    {statusLabel(run.status) || "ריצה"}
                  </Link>
                  <span className="text-gray-400"> · {fmtAbs(run.created_at) || "זמן לא תועד"}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }
  }
  return (
    <tr id={`evidence-${check.id}`} className="bg-gray-50 border-b last:border-0">
      <td colSpan={colSpan} className="px-4 py-3 space-y-2">
        <div className="text-xs font-medium text-gray-700">עדויות הרצה — {check.name}</div>
        {body}
      </td>
    </tr>
  );
}

function CheckRow({ check, agent, runsState, runs, now, expanded, onToggle }) {
  const absLast = fmtAbs(check.last_run_at);
  const historyHref = `/agent-runs?agent_id=${check.agent_id}&initiated_by_type=auto:scheduled_check`;
  const ownerName = check.agent_name || "עובד לא מזוהה";
  return (
    <>
      <tr className="border-b last:border-0 hover:bg-gray-50 align-top">
        <td className="px-4 py-3">
          <div className="flex items-start gap-2">
            <span className="mt-1.5"><HealthDot check={check} /></span>
            <div>
              <div className="font-medium text-gray-800">{check.name}</div>
              {check.description && <div className="text-xs text-gray-400">{check.description}</div>}
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <EmployeeAvatar name={ownerName} avatarKey={agent?.avatar_key} avatarColor={agent?.avatar_color} size="sm" />
            <div className="min-w-0">
              <Link to={`/agents/${check.agent_id}`} className="text-brand-600 hover:underline text-sm">
                {ownerName}
              </Link>
              {agent?.role_title && <div className="text-xs text-gray-400">{agent.role_title}</div>}
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-gray-600 text-sm">{intervalLabel(check.interval_hours) || "—"}</td>
        <td className="px-4 py-3 text-sm">
          {check.latest_run_id ? (
            <Link to={`/agent-runs/${check.latest_run_id}`} className={`hover:underline ${runStatusTone(check.latest_run_status)}`}>
              {statusLabel(check.latest_run_status) || "ריצה"}
            </Link>
          ) : (
            <span className="text-gray-400">טרם הורץ</span>
          )}
          <div className="text-xs text-gray-400">{absLast || "זמן לא תועד"}</div>
        </td>
        <td className="px-4 py-3 text-sm">
          <NextRunCell check={check} now={now} />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-3 justify-end">
            <Link
              to={historyHref}
              aria-label={`היסטוריית הרצות מתוזמנות — ${check.name}`}
              className="text-xs text-gray-500 hover:underline whitespace-nowrap"
            >
              היסטוריה
            </Link>
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls={`evidence-${check.id}`}
              onClick={onToggle}
              className="text-xs text-brand-600 hover:underline whitespace-nowrap"
            >
              {expanded ? "סגור עדויות" : "הצג עדויות"}
            </button>
          </div>
        </td>
      </tr>
      {expanded && <CheckEvidenceRow check={check} runsState={runsState} runs={runs} colSpan={6} />}
    </>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ScheduledOperationsCenterPage() {
  const [healthFilter, setHealthFilter] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const checksQuery = useQuery({
    queryKey: ["scheduled-checks"],
    queryFn: scheduledChecksApi.list,
    refetchInterval: 30000,
  });

  const checksLoaded = Array.isArray(checksQuery.data);
  const runsQuery = useQuery({
    queryKey: ["scheduled-operations", "runs"],
    queryFn: () => agentRunsApi.listAll({ initiated_by_type: "auto:scheduled_check", limit: RUNS_LIMIT }),
    refetchInterval: 60000,
    enabled: checksLoaded,
  });

  // Employee identity (avatar/persona) enriches the owner column; its failure silently degrades to
  // the checks' own agent_name, so it is never surfaced as an error.
  const agentsQuery = useQuery({
    queryKey: ["agents"],
    queryFn: () => agentsApi.list(),
    staleTime: 30_000,
    enabled: checksLoaded,
  });

  const checks = useMemo(() => checksQuery.data ?? [], [checksQuery.data]);
  const agentsById = useMemo(() => {
    const map = new Map();
    for (const agent of agentsQuery.data ?? []) map.set(agent.id, agent);
    return map;
  }, [agentsQuery.data]);

  const runs = Array.isArray(runsQuery.data) ? runsQuery.data : null;
  const runsState = {
    loading: runsQuery.isLoading,
    error: runsQuery.isError && !runsQuery.data,
    forbidden: runsQuery.error?.response?.status === 403,
    kind: runsQuery.error ? fetchFailureKind(runsQuery.error) : null,
  };

  const counts = useMemo(() => {
    const map = { overdue: 0, failed: 0, pending: 0, running: 0, paused: 0 };
    for (const check of checks) {
      const health = scheduleHealth(check);
      if (map[health] != null) map[health] += 1;
    }
    return map;
  }, [checks]);

  const sorted = useMemo(() => sortChecksForOps(checks), [checks]);
  const visibleChecks = healthFilter ? sorted.filter((c) => scheduleHealth(c) === healthFilter) : sorted;

  const upcoming = useMemo(() => upcomingChecks(checks), [checks]);
  const checkById = useMemo(() => {
    const map = new Map();
    for (const check of checks) map.set(String(check.id), check);
    return map;
  }, [checks]);

  const failures = failedRuns(runs);
  const unmatched = unmatchedSystemRuns(runs);
  // A refresh that fails after a successful load keeps the last data on screen: it must be
  // labelled as stale, never presented as current.
  const stale = checksQuery.isError && checksLoaded;
  const runsStale = runsQuery.isError && runs != null;

  const refetchAll = () => {
    if (refreshing) return;
    setRefreshing(true);
    const jobs = [checksQuery.refetch()];
    if (checksLoaded) jobs.push(runsQuery.refetch());
    Promise.allSettled(jobs).finally(() => setRefreshing(false));
  };

  if (checksQuery.isLoading) {
    return (
      <div className="flex justify-center py-16" dir="rtl">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (fetchFailureKind(checksQuery.error) === "forbidden" && !checksLoaded) {
    return (
      <div className="max-w-3xl mx-auto p-6" dir="rtl">
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-600">
          <p className="font-medium">אין הרשאה לצפות במרכז תפעול מתוזמן</p>
          <p className="text-sm text-gray-400 mt-1">ניהול הבדיקות המתוזמנות זמין למנהלים בלבד.</p>
        </div>
      </div>
    );
  }

  if (checksQuery.isError && !checksLoaded) {
    return (
      <div className="max-w-3xl mx-auto p-6" dir="rtl">
        <div role="alert" className="bg-white rounded-xl shadow-sm border p-8 text-center text-red-600">
          <p className="font-medium">לא ניתן לטעון את הבדיקות המתוזמנות</p>
          <p className="text-sm text-gray-500 mt-1">{FETCH_FAILURE_TEXT[fetchFailureKind(checksQuery.error)]}</p>
          <p className="text-sm text-gray-500">לא ידוע מה מתוזמן לרוץ — אין להסיק שאין בדיקות.</p>
          <button type="button" onClick={() => checksQuery.refetch()} className="mt-3 text-sm text-brand-600 hover:underline">
            נסה שוב
          </button>
        </div>
      </div>
    );
  }

  if (checks.length === 0) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-4" dir="rtl">
        {stale && (
          // The empty list is the last load that succeeded, not necessarily the current one.
          <p role="status" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            הרענון האחרון של רשימת הבדיקות נכשל — הנתונים המוצגים הם מהטעינה האחרונה שהצליחה.
          </p>
        )}
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center text-gray-400">
          <p className="text-3xl mb-3">📅</p>
          <p>אין בדיקות מתוזמנות מוגדרות</p>
          <p className="text-xs mt-1">
            הגדרת בדיקות מתוזמנות מתבצעת ב
            <Link to="/scheduled-checks" className="text-brand-600 hover:underline mx-1">
              בדיקות מתוזמנות
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1200px] mx-auto px-4 py-6 space-y-5" dir="rtl">
      <header className="space-y-1">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-gray-800">מרכז תפעול מתוזמן</h1>
          <button
            type="button"
            onClick={refetchAll}
            disabled={refreshing}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50"
          >
            {refreshing ? "מרענן…" : "רענן"}
          </button>
        </div>
        <p className="text-sm text-gray-500">
          מה מתוזמן לרוץ, מי העובד האחראי, מה קרה בהרצה האחרונה ומה נכשל — וקישור להמשך העבודה בכל שורה.
          התצוגה לקריאה בלבד; שינוי לוח הזמנים מתבצע ב
          <Link to="/scheduled-checks" className="text-brand-600 hover:underline mx-1">
            בדיקות מתוזמנות
          </Link>
          .
        </p>
        {stale && (
          <p role="status" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            הרענון האחרון של רשימת הבדיקות נכשל — הנתונים המוצגים הם מהטעינה האחרונה שהצליחה.
          </p>
        )}
        {runsStale && (
          <p role="status" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            הרענון האחרון של היסטוריית ההרצות נכשל — העדויות והכשלים המוצגים הם מהטעינה האחרונה שהצליחה.
          </p>
        )}
      </header>

      {/* KPI tiles — click to filter the table */}
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="סינון לפי מצב בדיקה">
        <button
          type="button"
          aria-pressed={healthFilter === null}
          onClick={() => setHealthFilter(null)}
          className={`text-xs rounded-full px-3 py-1.5 border ${
            healthFilter === null ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
          }`}
        >
          הצג הכל ({checks.length})
        </button>
        {TILES.map((tile) => (
          <button
            key={tile.key}
            type="button"
            aria-pressed={healthFilter === tile.key}
            onClick={() => setHealthFilter((current) => (current === tile.key ? null : tile.key))}
            className={`text-xs rounded-full px-3 py-1.5 border ${
              healthFilter === tile.key ? "ring-2 ring-gray-700 " : ""
            }${tile.cls}`}
          >
            {tile.label} ({counts[tile.key]})
          </button>
        ))}
      </div>

      {/* Upcoming jobs */}
      <section aria-label="הרצות מתוזמנות קרובות" className="space-y-2">
        <div className="flex items-center gap-2">
          <span aria-hidden="true">⏭️</span>
          <h2 className="text-base font-semibold text-gray-800">הרצות מתוזמנות קרובות</h2>
        </div>
        {upcoming.length === 0 ? (
          <p className="text-sm text-gray-400">אין בדיקות פעילות עם מועד הרצה ידוע.</p>
        ) : (
          <ul className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y">
            {upcoming.map((check) => {
              const rel = formatRelativeHe(check.next_run_at, now);
              const late = rel != null && rel.startsWith("באיחור");
              return (
                <li key={check.id} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <EmployeeAvatar
                      name={check.agent_name || "עובד לא מזוהה"}
                      avatarKey={agentsById.get(check.agent_id)?.avatar_key}
                      avatarColor={agentsById.get(check.agent_id)?.avatar_color}
                      size="sm"
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">{check.name}</div>
                      <div className="text-xs text-gray-400">
                        <Link to={`/agents/${check.agent_id}`} className="hover:underline">
                          {check.agent_name || "עובד לא מזוהה"}
                        </Link>
                        {" · "}
                        {intervalLabel(check.interval_hours) || "תדירות לא ידועה"}
                      </div>
                    </div>
                  </div>
                  <div className={`text-xs ${late ? "text-orange-600" : "text-gray-500"}`}>
                    {fmtAbs(check.next_run_at)}
                    {rel && <span className="text-gray-400"> · {rel}</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Checks table: last job, outcome, next run, owner, evidence */}
      <section aria-label="בדיקות והרצות אחרונות" className="space-y-2">
        <div className="flex items-center gap-2">
          <span aria-hidden="true">🗓️</span>
          <h2 className="text-base font-semibold text-gray-800">בדיקות והרצות אחרונות</h2>
          <span className="text-xs text-gray-400">({visibleChecks.length} מתוך {checks.length})</span>
        </div>
        {visibleChecks.length === 0 ? (
          <p className="text-sm text-gray-400">אין בדיקות במצב הנבחר.</p>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-right px-4 py-3">בדיקה</th>
                  <th className="text-right px-4 py-3">עובד אחראי</th>
                  <th className="text-right px-4 py-3">תדירות</th>
                  <th className="text-right px-4 py-3">הרצה אחרונה</th>
                  <th className="text-right px-4 py-3">הרצה הבאה</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {visibleChecks.map((check) => (
                  <CheckRow
                    key={check.id}
                    check={check}
                    agent={agentsById.get(check.agent_id)}
                    runsState={runsState}
                    runs={runs}
                    now={now}
                    expanded={expandedId === check.id}
                    onToggle={() => setExpandedId((current) => (current === check.id ? null : check.id))}
                  />
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </section>

      {/* Failures */}
      <section aria-label="כשלים בהרצות מתוזמנות" className="space-y-2">
        <div className="flex items-center gap-2">
          <span aria-hidden="true">⚠️</span>
          <h2 className="text-base font-semibold text-gray-800">כשלים בהרצות מתוזמנות</h2>
        </div>
        {runsState.loading ? (
          <p className="text-sm text-gray-400">טוען היסטוריית הרצות…</p>
        ) : runsState.forbidden ? (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">{RUNS_FORBIDDEN_TEXT}</p>
        ) : runsState.error ? (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">{runsUnavailableText(runsState.kind)}</p>
        ) : failures.length === 0 ? (
          <p className="text-sm text-gray-400">{NO_FAILURES_TEXT}</p>
        ) : (
          <div className="space-y-2">
            <ul className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y">
              {failures.slice(0, FAILURES_SHOWN).map((run) => {
                const check = checkById.get(String(run?.context?.scheduled_check_id ?? ""));
                const summary = safeText(run.result_summary, SUMMARY_MAX);
                return (
                  <li key={run.id} className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 space-y-0.5">
                      <div className="text-sm font-medium text-gray-800">
                        {check ? check.name : "ריצת מערכת (ללא בדיקה מוגדרת)"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {run.agent_name || "עובד לא מזוהה"} · {fmtAbs(run.created_at) || "זמן לא תועד"} ·{" "}
                        {statusLabel(run.status) || "סטטוס לא ידוע"}
                      </div>
                      <div className={`text-xs ${summary ? "text-gray-500" : "text-gray-400 italic"}`}>
                        {summary || "לא תועדה סיבת כשל"}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {run.ticket_id && (
                        <Link to={`/tickets/${run.ticket_id}`} className="text-xs text-brand-600 hover:underline whitespace-nowrap">
                          {run.ticket_number != null ? `קריאה #${run.ticket_number}` : "לקריאה"}
                        </Link>
                      )}
                      <Link
                        to={`/agent-runs/${run.id}`}
                        aria-label={`טפל בכשל — ${check ? check.name : "ריצת מערכת"}`}
                        className="text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg px-3 py-1.5 whitespace-nowrap"
                      >
                        טפל בכשל
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
            {failures.length > FAILURES_SHOWN && (
              <p className="text-xs text-gray-400">
                מוצגים {FAILURES_SHOWN} הכשלים האחרונים מתוך {failures.length} ב-{RUNS_LIMIT} ההרצות שנטענו.
              </p>
            )}
          </div>
        )}
      </section>

      {/* System runs without a defined check (e.g. Guardian posture runs) */}
      {runs != null && unmatched.length > 0 && (
        <section aria-label="הרצות מערכת ללא בדיקה מוגדרת" className="space-y-2">
          <div className="flex items-center gap-2">
            <span aria-hidden="true">🛡️</span>
            <h2 className="text-base font-semibold text-gray-800">הרצות מערכת ללא בדיקה מוגדרת</h2>
            <span className="text-xs text-gray-400">({unmatched.length})</span>
          </div>
          <p className="text-xs text-gray-500">
            הרצות מערכת אוטומטיות (כגון סריקת מצב של Guardian) שאינן משויכות לבדיקה מתוזמנת מוגדרת — אין להן בדיקה או מועד משלהן.
          </p>
          <ul className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y">
            {unmatched.slice(0, UNMATCHED_SHOWN).map((run) => (
              <li key={run.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="text-xs text-gray-600">
                  <span className="text-sm text-gray-800">{run.agent_name || "עובד לא מזוהה"}</span>
                  {" · "}
                  {fmtAbs(run.created_at) || "זמן לא תועד"}
                </div>
                <Link to={`/agent-runs/${run.id}`} className={`text-xs hover:underline whitespace-nowrap ${runStatusTone(run.status)}`}>
                  {statusLabel(run.status) || "ריצה"}
                </Link>
              </li>
            ))}
          </ul>
          {unmatched.length > UNMATCHED_SHOWN && (
            <p className="text-xs text-gray-400">מוצגות {UNMATCHED_SHOWN} ההרצות האחרונות מתוך {unmatched.length}.</p>
          )}
        </section>
      )}
    </div>
  );
}
